import { useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../../store';
import {
    fetchLeadStages,
    selectAllStages,
    selectStagesLoaded,
    selectStagesLoading,
} from '../store/slices/stageMasterSlice';
import type { LeadStageMaster } from '../types';

/**
 * Custom hook for accessing dynamic lead stages.
 *
 * This hook:
 * - Fetches stages on first use (if not already loaded)
 * - Provides computed helpers (getStageByKey, getStageByOrder, stageMap, etc.)
 * - Can be used anywhere stages are needed
 *
 * Usage:
 *   const { stages, getStageByKey, isOptional, getStageLabel } = useLeadStages();
 */
export function useLeadStages() {
    const dispatch = useAppDispatch();
    const stages = useAppSelector(selectAllStages);
    const loaded = useAppSelector(selectStagesLoaded);
    const loading = useAppSelector(selectStagesLoading);

    // Fetch on first use
    useEffect(() => {
        if (!loaded && !loading) {
            dispatch(fetchLeadStages(false));
        }
    }, [dispatch, loaded, loading]);

    // Computed values (memoized)
    const computed = useMemo(() => {
        // Stage map: key → stage object
        const stageMap = new Map<string, LeadStageMaster>();
        stages.forEach(s => stageMap.set(s.key, s));

        // Stage order map: stageOrder → key
        const orderToKey = new Map<number, string>();
        stages.forEach(s => orderToKey.set(s.stageOrder, s.key));

        // Key → order map
        const keyToOrder = new Map<string, number>();
        stages.forEach(s => keyToOrder.set(s.key, s.stageOrder));

        // Filtered lists
        const fixedStages = stages.filter(s => s.stageType === 'fixed');
        const optionalStages = stages.filter(s => s.stageType === 'optional');
        const terminalStages = stages.filter(s => s.stageType === 'terminal');
        const nonTerminalStages = stages.filter(s => s.stageType !== 'terminal');

        // Unique stepper labels (terminal stages share one label)
        const stepperStages: Array<{ key: string; label: string; stages: LeadStageMaster[] }> = [];
        const seenLabels = new Set<string>();
        stages.forEach(s => {
            const label = s.stepperLabel || s.label;
            if (!seenLabels.has(label)) {
                seenLabels.add(label);
                stepperStages.push({
                    key: s.key,
                    label,
                    stages: stages.filter(st => (st.stepperLabel || st.label) === label),
                });
            }
        });

        return {
            stageMap,
            orderToKey,
            keyToOrder,
            fixedStages,
            optionalStages,
            terminalStages,
            nonTerminalStages,
            stepperStages,
        };
    }, [stages]);

    // Helper functions
    const getStageByKey = (key: string): LeadStageMaster | undefined => {
        return computed.stageMap.get(key);
    };

    const getStageByOrder = (order: number): LeadStageMaster | undefined => {
        const key = computed.orderToKey.get(order);
        return key ? computed.stageMap.get(key) : undefined;
    };

    const getStageLabel = (key: string): string => {
        return computed.stageMap.get(key)?.label || key;
    };

    const getStepperLabel = (key: string): string => {
        const stage = computed.stageMap.get(key);
        return stage?.stepperLabel || stage?.label || key;
    };

    const getStageOrder = (key: string): number => {
        return computed.keyToOrder.get(key) || 0;
    };

    const isOptional = (key: string): boolean => {
        return computed.stageMap.get(key)?.stageType === 'optional';
    };

    const isTerminal = (key: string): boolean => {
        return computed.stageMap.get(key)?.stageType === 'terminal';
    };

    const supportsSubStatus = (key: string): boolean => {
        return computed.stageMap.get(key)?.supportsSubStatus === true;
    };

    const getSubStatuses = (key: string) => {
        return computed.stageMap.get(key)?.subStatuses || [];
    };

    const getDependencies = (key: string): string[] => {
        return computed.stageMap.get(key)?.dependencies || [];
    };

    const getAllowedActions = (key: string): string[] => {
        return computed.stageMap.get(key)?.allowedActions || [];
    };

    /**
     * Check if a stage is visible in the stepper based on current lead state
     */
    const isStageVisible = (
        stageKey: string,
        currentDetailedStage?: string,
        followUpHistory?: Array<{ type?: string; channel?: string; date: string }>
    ): boolean => {
        const stage = computed.stageMap.get(stageKey);
        if (!stage) return false;

        // Fixed and terminal stages are always visible
        if (stage.stageType === 'fixed' || stage.stageType === 'terminal') {
            return true;
        }

        // Optional: visible if current stage
        if (currentDetailedStage === stageKey) return true;

        // Optional: visible if the current stage is beyond this one
        if (currentDetailedStage) {
            const currentOrder = getStageOrder(currentDetailedStage);
            const thisOrder = getStageOrder(stageKey);
            if (currentOrder > thisOrder) return true;
        }

        // Optional: visible if found in follow-up history
        if (followUpHistory?.length) {
            const label = getStageLabel(stageKey).toLowerCase();
            for (const activity of followUpHistory) {
                const channel = (activity.channel || '').toLowerCase();
                // Check if channel contains the stage label words
                if (channel.includes(label)) return true;
                // Check by type
                if (activity.type === stageKey) return true;
            }
        }

        return false;
    };

    /**
     * Get the status of a stage relative to the current stage
     */
    const getStageStatus = (
        stageKey: string,
        currentDetailedStage?: string,
        currentSubStatus?: string
    ): 'completed' | 'current' | 'upcoming' => {
        if (!currentDetailedStage) return 'upcoming';

        // if (stageKey === currentDetailedStage) return 'current';

        const currentOrder = getStageOrder(currentDetailedStage);
        const stageOrder = getStageOrder(stageKey);

        if (stageOrder < currentOrder) return 'completed';

        // For the current stage — check sub-status
        if (stageKey === currentDetailedStage) {
            if (!currentSubStatus || currentSubStatus === 'completed' || currentSubStatus === 'approved') {
                return 'completed';
            }
            return 'current';
        }

        return 'upcoming';
    };

    return {
        // Data
        stages,
        loading,
        loaded,
        ...computed,

        // Helpers
        getStageByKey,
        getStageByOrder,
        getStageLabel,
        getStepperLabel,
        getStageOrder,
        isOptional,
        isTerminal,
        supportsSubStatus,
        getSubStatuses,
        getDependencies,
        getAllowedActions,
        isStageVisible,
        getStageStatus,
    };
}
